import { action, computed, observable } from 'mobx';
import { chunk } from 'lodash';
import {
  AcceptedModelInputs,
  ApiResponse,
  Collection,
  CollectionOptions,
  DiscreteFilter,
  FetchOptions,
  ModelSelectOptions
} from 'core/model/Collection';
import GenericModel, { Model } from 'core/model/Model';

export interface PageState {
  offset: number;
  pageCount: number;
  // TODO currentPage vs page is messy nonsense but out of scope for TS conversion
  currentPage: number;
  page?: number;

  perPage: number;
  totalCount: number;
  pageCountLoaded: boolean;
  groupedDataFracturedKey?: string;
}

export interface PagedModelSelectOptions extends ModelSelectOptions {
  selectedLimit?: number;
}

// TODO fetchUrl vs url and httpMethod vs fetchMethod is an unnecessary footgun
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface PagedFetchOptions<MOD extends Model<any, any>> extends Omit<
  FetchOptions<MOD>,
  'fetchUrl' | 'fetchMethod'
> {
  url?: string;
  httpMethod?: 'get' | 'post';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export abstract class PagedCollection<MOD extends Model<any, any>> extends Collection<MOD> {
  @observable
  pageState: PageState = {
    offset: 0,
    pageCount: 1,
    currentPage: 1,
    perPage: 10,
    totalCount: 0,
    pageCountLoaded: false,
    groupedDataFracturedKey: undefined
  };

  // should searchState and associated move up to Collection? Leaving here for now until searchState get solidified
  @observable
  searchState: { value?: string } = {};

  constructor(data: AcceptedModelInputs<MOD>[] = [], options: CollectionOptions<MOD> = {}) {
    super(data, options);
    this.processData = this.processData.bind(this);
    this.reset = this.reset.bind(this);

    this.calculatePageCount();
  }

  get pagedFetchMethod(): 'get' | 'post' {
    return 'post';
  }

  @action
  setPageState(state: Partial<PageState> = {}) {
    this.pageState = Object.assign(this.pageState, state);
  }

  @action
  setSearchState(state: { value?: string } = {}) {
    this.searchState = Object.assign(this.searchState, state);
  }

  @computed
  get pageModels(): MOD[] | undefined {
    const { page, perPage } = this.pageState;

    const chunks = chunk(this.models, perPage);
    return page !== undefined ? chunks[page] : undefined;
  }

  /**
   * Just a shortcut helper for setting pageState offset and currentPage values when pagination page is selected.
   * @param page
   */
  @action
  setPage(page: number) {
    this.setPageState({ offset: Math.ceil((page - 1) * this.pageState.perPage), currentPage: page });
  }

  @action
  applyPageChangeFilter() {
    const { currentPage, perPage } = this.pageState;

    const paginationDiscreteFilter = {
      type: 'pagination',
      fn: (_model: MOD, index: number) => index >= (currentPage - 1) * perPage && index < currentPage * perPage
    };

    this.addDiscreteFilter(paginationDiscreteFilter);
    this.filter();
  }

  @action
  calculatePageCount() {
    // remove pagination filter to ensure correct unfiltered size (not just 10 results per page)
    this.removeDiscreteFilter('pagination');
    this.filter();

    const { perPage, currentPage } = this.pageState;
    const pageCount = Math.ceil(this.size / perPage) || 1;

    this.setPageState({
      totalCount: this.size,
      pageCount,
      currentPage: currentPage > pageCount ? 1 : currentPage
    });
  }

  /**
   * Resets all pageState EXCEPT for perPage back to initial values
   */
  @action
  resetPageState() {
    this.setPageState({ offset: 0, pageCount: 1, currentPage: 1, totalCount: 0, pageCountLoaded: false });
  }

  @action
  processData(response: ApiResponse<MOD> | null) {
    if (!response) {
      return super.processData(response);
    }
    const { metadata, pagination } = response;
    const totalCount = (metadata && metadata.totalCount) || (pagination && Number(pagination.total_count)) || 0;
    let pageCount = 1;

    if (totalCount && !Number.isNaN(totalCount) && totalCount > 0) {
      pageCount = Math.ceil(totalCount / this.pageState.perPage);
    }

    // if we have a 0 total count and more results, this state will be managed elsewhere (don't update here)
    if (!(totalCount === 0 && pagination?.has_more_results)) {
      this.setPageState({ totalCount, pageCount });
    }

    return super.processData(response);
  }

  @action
  selectAll(options: PagedModelSelectOptions = {}) {
    const { pageCount } = this.pageState;
    const { selectedLimit = Number.MAX_VALUE } = options;

    if (pageCount === 1) {
      super.selectAll();
      return;
    }

    this.models.forEach((model) => {
      if (this.selectedSize >= selectedLimit) {
        return;
      }

      model.select(options);
    });
  }

  @action
  reset(options = {}) {
    this.setPageState({ offset: 0, pageCount: 1, currentPage: 1, perPage: 10, totalCount: 0 });
    this.searchState = {};
    return super.reset(options);
  }

  /**
   * Just a helper so don't have to specify search options on queuedFetch all the time.
   */
  @action
  async pagedFetch(options: PagedFetchOptions<MOD> = {}) {
    const { data, url, httpMethod, preserveSelection, query } = options;

    const pfHttpMethod = httpMethod || this.pagedFetchMethod;
    const pfUrl = url || (this.urlPaths && this.urlPaths.pagedFetch ? this.urlPaths.pagedFetch : this.url);

    return this.queuedFetch({ data, fetchUrl: pfUrl, fetchMethod: pfHttpMethod, preserveSelection, query });
  }

  addDiscreteFilter = (discreteFilter: DiscreteFilter<MOD>) => {
    this.removeDiscreteFilter(discreteFilter.type);
    this.setDiscreteFilters([...this.discreteFilters, discreteFilter]);
  };

  applyPagination() {
    this.calculatePageCount();
    this.applyPageChangeFilter();
  }

  @action
  resetDiscreteFilters() {
    super.resetDiscreteFilters();
    this.applyPagination();
  }
}

/**
 * @deprecated prefer a typed PagedCollection
 */
export default class GenericPagedCollection extends PagedCollection<GenericModel> {
  // Provide a default implementation for the model getter
  get model() {
    return GenericModel;
  }
}
